import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AnalyticsEvent, track } from '@bonusly/analytics';
import { useHttpClient } from '@bonusly/http-client';
import { useCurrentUser, useFeatureFlag, useIdentity } from '@bonusly/who-am-i';

import { QueryKey as RecognitionBonusQueryKey } from '../../../components/recognition/bonus/hooks/types';
import { useBizyChannel } from '../../../hooks/use-bizy-channel';
import { executePageAction, useActionSchemaRegistry } from '../../../lib/bizy-actions';
import { useBizyHttpClient } from '../../../lib/bizy/bizy-http-client';
import { useBizyChatState, useBizyTimeline, type BizyResponseReceivedMeta } from '../../../lib/bizy/connector';
import type {
	ApprovalDecision,
	BizyAssistantMessageNode,
	BizyChatMessage,
	BizyUserConversationDetail,
	BizyUserMessageNode,
	CardNode,
	ChatMessage,
	FeedbackType,
} from '../../../lib/bizy/types';
import { QueryKey as RecognitionFeedQueryKey } from '../../../modules/bonus-feed/features/recognition/api/hooks/types';
import { QueryKey as GrowthQueryKey } from '../../../modules/growth/types/growth-query-key';
import { bizyConversationPath, useBizyUserConversation } from '../../../queries/bizy-conversations';
import { bizyUnreadNotificationsQueryKey } from '../../../queries/bizy-notifications';
import { bizyPeopleQueryKey } from '../../../queries/bizy-people';
import { invalidateCelebrationsAdminCaches } from '../../../queries/celebrations';
import type { BizyState } from '../../bizy';
import type { NavigateFn } from '../bizy-navigation-context';
import { markBizyActivity } from '../lib/bizy-activity-stamp';
import { clearBizySession, loadBizySession, saveBizySession } from '../lib/session-storage';
import { useBizyWidgetStore } from '../store/use-bizy-widget-store';
import { useBizyReportContext } from './use-bizy-report-context';

export type { ApprovalDecision, CardNode, ChatMessage, FeedbackType };

// A conversation the user idled away from and then replaced via a blank-slate
// send (BIZY-683, classic mode only). Stays rendered above a time divider
// while the new backend conversation lives below it. In-memory only — the
// archived view doesn't survive reloads.
export interface BizyArchivedSegment {
	messages: ChatMessage[];
	boundaryAt: Date;
}

// The blank-slate divider caps the OLD conversation, so it's labeled with the
// conversation's last stamped turn — not when the fresh start appeared (a
// day-old thread must not read "a minute ago"). Falls back to "now" only when
// nothing is stamped (proactive-only timelines), where it's the least-wrong label.
export function newestMessageTimestamp(messages: ChatMessage[]): Date | null {
	for (let i = messages.length - 1; i >= 0; i--) {
		const turnTimestamp = messages[i].timestamp;
		// Session-restored timestamps arrive as ISO strings at runtime.
		if (turnTimestamp) return new Date(turnTimestamp);
	}
	return null;
}

interface UseBizyGlobalChatOptions {
	/**
	 * Extra fields merged into every chat POST's `context`. A getter is
	 * resolved when the POST body is built (send time), letting callers clear
	 * volatile entry-point state synchronously before a send that must not
	 * carry it — a plain object is captured per render as before.
	 */
	extraContext?: Record<string, string> | (() => Record<string, string>);
	model?: string;
	/** Override React Router navigate for non-SPA contexts (e.g. MFE on Rails pages). */
	navigateFn?: NavigateFn;
}

const EMOTION_CURIOUS: BizyState = 'curious';

let messageKeyCounter = 0;
function nextMessageKey(): string {
	return `msg-${Date.now()}-${messageKeyCounter++}`;
}

// The attach_gif turn posts this exact user message (see the dispatcher's
// `attach_gif` case). Matching it scopes the user-bubble thumbnail to attach
// turns only — a later plain-text turn whose re-emitted draft card happens to
// carry an attachment must not borrow that gif onto its bubble.
const ATTACH_GIF_USER_MESSAGE = 'Use this gif';

// Surface the picked gif as the user-bubble thumbnail so it survives a
// refetch/reload. Scoped to the attach_gif turn.
function userTurnGifPreview(userMessage: string, nodes?: BizyAssistantMessageNode[]): string | undefined {
	if (userMessage !== ATTACH_GIF_USER_MESSAGE) return undefined;
	const card = nodes?.find((node) => node.type === 'recognition_draft_card');
	const attachment = (card?.payload as { attachment?: { gif?: string; mp4?: string } } | undefined)?.attachment;
	return attachment?.gif || attachment?.mp4 || undefined;
}

interface BizyMessageLike {
	userMessage: string | null;
	bizyResponse: string;
	id?: string;
	feedbackType?: FeedbackType | null;
	createdAt?: string | Date;
	assistantMessage?: BizyAssistantMessageNode[] | null;
}

// Hydrates a stored Bizy message into canonical `BizyChatMessage` turns. Skips
// the user bubble for proactive (assistant-first) records. Unlike the package's
// `hydrateMessages`, this preserves the app's behavior of leaving `nodes` empty
// for legacy rows (no synthetic markdown node) and stamps the attach_gif
// user-bubble thumbnail.
function hydrateBizyMessage(msg: BizyMessageLike): BizyChatMessage[] {
	const nodes = msg.assistantMessage?.length ? msg.assistantMessage : [];
	const turns: BizyChatMessage[] = [];
	if (msg.userMessage) {
		turns.push({
			role: 'user',
			content: msg.userMessage,
			key: nextMessageKey(),
			timestamp: msg.createdAt ? new Date(msg.createdAt) : undefined,
			attachmentPreviewUrl: userTurnGifPreview(msg.userMessage, nodes),
		});
	}
	turns.push({
		role: 'assistant',
		content: msg.bizyResponse,
		id: msg.id,
		feedbackType: msg.feedbackType ?? undefined,
		key: nextMessageKey(),
		nodes,
	});
	return turns;
}

// ── union ⇆ flat mapping ──────────────────────────────────────────────
// The shared connector works in the `BizyChatMessage` discriminated union
// (assistant nodes on `nodes`); the chat view + session storage still consume
// the legacy flat `ChatMessage` (`assistantMessage`). Map at the hook boundary
// so consumers are untouched.

function toFlat(message: BizyChatMessage): ChatMessage {
	if (message.role === 'user') {
		return {
			role: 'user',
			content: message.content,
			key: message.key,
			timestamp: message.timestamp,
			attachmentPreviewUrl: message.attachmentPreviewUrl,
			nodes: message.nodes,
		};
	}
	return {
		role: 'assistant',
		content: message.content,
		id: message.id,
		key: message.key,
		feedbackType: message.feedbackType,
		nodeChoices: message.nodeChoices,
		toolsUsed: message.toolsUsed,
		timestamp: message.timestamp,
		assistantMessage: message.nodes.length ? message.nodes : undefined,
	};
}

function toUnion(message: ChatMessage): BizyChatMessage {
	if (message.role === 'user') {
		return {
			role: 'user',
			content: message.content,
			key: message.key ?? nextMessageKey(),
			timestamp: message.timestamp,
			attachmentPreviewUrl: message.attachmentPreviewUrl,
			nodes: message.nodes,
		};
	}
	return {
		role: 'assistant',
		content: message.content,
		id: message.id,
		key: message.key ?? nextMessageKey(),
		feedbackType: message.feedbackType,
		nodeChoices: message.nodeChoices,
		toolsUsed: message.toolsUsed,
		timestamp: message.timestamp,
		nodes: message.assistantMessage ?? [],
	};
}

const PATH = () => window.location.pathname;

// Returns the subset of `incoming` turns that are genuinely newer than anything
// in `current`, anchored on the newest known assistant message ID. Preserves
// user-bubble pairing — only turns after the last known assistant bubble are returned.
function mergeNewTurns(current: BizyChatMessage[], incoming: BizyChatMessage[]): BizyChatMessage[] {
	const newestKnownId = [...current].reverse().find((m) => m.role === 'assistant')?.id;
	if (!newestKnownId) return incoming;
	const anchorIdx = incoming.findIndex((m) => m.role === 'assistant' && m.id === newestKnownId);
	if (anchorIdx === -1) return incoming;
	return incoming.slice(anchorIdx + 1);
}

/**
 * Bizy streaming chat for admin pages. Thin app adapter over the shared
 * `useBizyChatState` connector (which owns the streaming state machine,
 * timeout/disconnect, request-id isolation, and the chat POST/cancel). This
 * layer adds the Kaleidoscope-specific concerns: session persistence,
 * conversation load/resume + cross-tab refetch, page actions, report context,
 * analytics, navigation, and the union⇆flat message mapping.
 */
export function useBizyGlobalChat(options: UseBizyGlobalChatOptions = {}) {
	const { extraContext, model, navigateFn } = options;
	const sessionData = loadBizySession();

	const isContinuous = useFeatureFlag('bizy-continuous-chat');

	const currentUser = useCurrentUser();
	const http = useHttpClient();
	const bizyHttp = useBizyHttpClient();
	const queryClient = useQueryClient();
	const reportContext = useBizyReportContext((state) => state.context);
	const resumeConversationId = useBizyWidgetStore((state) => state.resumeConversationId);
	const clearResume = useBizyWidgetStore((state) => state.clearResume);
	const { refetchProfile } = useIdentity();
	const routerNavigate = useNavigate();
	const navigate = navigateFn ?? routerNavigate;
	const { getSchema } = useActionSchemaRegistry();

	const refreshRecognitionSurfaces = useCallback(() => {
		queryClient.invalidateQueries([RecognitionFeedQueryKey.BonusesInfinite]);
		queryClient.invalidateQueries([RecognitionFeedQueryKey.Bonus]);
		queryClient.invalidateQueries([RecognitionFeedQueryKey.UserProfile]);
		queryClient.invalidateQueries([RecognitionBonusQueryKey.ProfileBonuses]);
		void refetchProfile();
	}, [queryClient, refetchProfile]);

	// Refs the async/page-action callbacks read synchronously. Synced from the
	// connector's reactive state below.
	const conversationIdRef = useRef<string | undefined>(sessionData?.conversationId);
	const isLoadingRef = useRef(false);
	const unionMessagesRef = useRef<BizyChatMessage[]>([]);
	const flatMessagesRef = useRef<ChatMessage[]>([]);
	const bizyEmotionRef = useRef<string>(EMOTION_CURIOUS);
	// Bumps on user-initiated actions so a slow in-flight refetch can't clobber
	// fresher state when it resolves.
	const refetchGenerationRef = useRef(0);

	// Page-action handler — navigation + cache invalidation. Injected into the
	// connector (it resets the stream timeout before invoking this).
	const handlePageAction = useCallback(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any -- the page_action payload is loosely typed at the wire
		async (data: any) => {
			try {
				if (data.action === 'navigate_to_page') {
					if (data.page_path) {
						const path = String(data.page_path);
						if (path.startsWith('/') && !path.startsWith('//')) {
							saveBizySession({
								messages: flatMessagesRef.current,
								conversationId: conversationIdRef.current,
								bizyEmotion: bizyEmotionRef.current as BizyState,
							});
							navigate(path);
							requestAnimationFrame(() => {
								const el = document.querySelector('[data-bizy-chat-scroll]');
								if (el) el.scrollTop = el.scrollHeight;
							});
						}
					}
					return;
				}
				if (data.action === 'invalidate_celebration_queries') {
					invalidateCelebrationsAdminCaches(queryClient);
					return;
				}
				if (data.action === 'invalidate_recognition_queries') {
					refreshRecognitionSurfaces();
					return;
				}
				if (data.action === 'invalidate_meetings_queries') {
					queryClient.invalidateQueries({ queryKey: [GrowthQueryKey.MeetingsV2] });
					return;
				}
				const schema = getSchema(data.report_type);
				await executePageAction(data, schema, navigate);
			} catch (error) {
				console.error('[Bizy] Failed to execute action:', error);
			}
		},
		[getSchema, navigate, queryClient, refreshRecognitionSurfaces]
	);

	// Compose the POST `context`: app surface + page path + report context +
	// hook-level extraContext + per-message overrides (highest precedence).
	const resolveContext = useCallback(
		(perMessageExtraContext?: Record<string, string>): Record<string, string> => ({
			surface: 'web',
			page_path: PATH(),
			...reportContext,
			...((typeof extraContext === 'function' ? extraContext() : extraContext) || {}),
			...(perMessageExtraContext || {}),
		}),
		[reportContext, extraContext]
	);

	const onMessageSent = useCallback((meta: { messageLength: number; conversationLength: number; isFirstMessage: boolean }) => {
		track(AnalyticsEvent.Bizy.ChatMessageSent, { ...meta, path: PATH() });
	}, []);

	const onResponseReceived = useCallback((meta: BizyResponseReceivedMeta) => {
		track(AnalyticsEvent.Bizy.ResponseReceived, { ...meta, path: PATH() });
	}, []);

	const onGenerationCancelled = useCallback(() => {
		track(AnalyticsEvent.Bizy.GenerationCancelled, { path: PATH() });
	}, []);

	// Seed the connector from the persisted session once.
	const initialMessages = useMemo(() => (sessionData?.messages ?? []).map(toUnion), []); // eslint-disable-line react-hooks/exhaustive-deps -- seed once from session

	// Continuous-chat timeline pager. Always called (hooks can't be conditional);
	// active only when `isContinuous` is true.
	const timeline = useBizyTimeline({ http: bizyHttp });

	const chat = useBizyChatState({
		http: bizyHttp,
		model,
		conversationId: sessionData?.conversationId,
		initialMessages,
		resolveContext,
		onPageAction: handlePageAction,
		onMessageSent,
		onResponseReceived,
		onGenerationCancelled,
		continuousMode: isContinuous,
	});

	// Map connector messages (union) → flat for consumers, and keep refs fresh.
	const messages = useMemo(() => chat.messages.map(toFlat), [chat.messages]);
	conversationIdRef.current = chat.conversationId;
	isLoadingRef.current = chat.isLoading;
	unionMessagesRef.current = chat.messages;
	flatMessagesRef.current = messages;
	bizyEmotionRef.current = chat.bizyEmotion;

	// Subscribe to BizyChannel; the connector supplies all the handlers. We
	// layer on `onObservedMomentSurfaced` here (not in the generic chat-state
	// connector) so the always-on panel subscription refreshes the People
	// Browser sidebar live when the dreaming pipeline surfaces a new moment.
	const channelOptions = useMemo(
		() => ({
			...chat.channelOptions,
			onObservedMomentSurfaced: () => {
				queryClient.invalidateQueries(bizyPeopleQueryKey());
				queryClient.invalidateQueries(bizyUnreadNotificationsQueryKey());
			},
		}),
		[chat.channelOptions, queryClient]
	);
	useBizyChannel(currentUser?.id, channelOptions);

	// ── Conversation load / resume ────────────────────────────────────
	const [loadConversationId, setLoadConversationId] = useState<string | undefined>();
	// Enabled only in legacy mode; disabled in continuous mode.
	const { data: loadedConversation, isError: isLoadError } = useBizyUserConversation(!isContinuous ? loadConversationId : undefined);

	// Conversations archived by blank-slate sends (BIZY-683, classic only).
	// Owned here — next to the adapter that owns conversation identity — so
	// every path that replaces or empties the timeline (new chat, conversation
	// load, resume, pagehide reset) clears the archive in the same breath and
	// the view can't drift out of sync with it.
	const [archivedSegments, setArchivedSegments] = useState<BizyArchivedSegment[]>([]);

	const loadConversation = useCallback(
		(id: string) => {
			// The incoming conversation replaces (classic) or extends (continuous,
			// where archives never exist) the timeline the archive was anchored to.
			setArchivedSegments([]);
			if (isContinuous) {
				// In continuous mode, proactive turns arrive on the timeline. Instead
				// of swapping to a specific conversation, refresh the newest page to
				// pick up the new turn. Merge rather than replace so any older turns
				// the user scrolled to are preserved.
				timeline
					.loadNewest()
					.then((messages) => {
						if (messages.length === 0) return;
						const newTurns = mergeNewTurns(unionMessagesRef.current, messages);
						if (newTurns.length === 0) return;
						const convId = conversationIdRef.current ?? id;
						chat.loadMessages([...unionMessagesRef.current, ...newTurns], { conversationId: convId, bizyEmotion: EMOTION_CURIOUS });
					})
					.catch(() => {});
				return;
			}
			setLoadConversationId(id);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[isContinuous]
	);

	// Resume a conversation picked in the history list / drawer (legacy mode only).
	useEffect(() => {
		if (!resumeConversationId) return;
		if (!isContinuous) {
			// A deliberate pick counts as conversation activity — deliberately
			// opening an old conversation must not immediately blank-slate it.
			markBizyActivity();
			setArchivedSegments([]);
			setLoadConversationId(resumeConversationId);
		}
		clearResume();
	}, [resumeConversationId, isContinuous, clearResume]);

	// ── Continuous-chat init ─────────────────────────────────────────
	// On first mount in continuous mode with no session, fetch the active
	// segment id and load the newest timeline page.
	const continuousInitRef = useRef(false);
	const [didInitLoad, setDidInitLoad] = useState(false);
	// Whether the *next* message would start a fresh chat segment rather than
	// append to the current one. Sourced from the server's 8h-boundary check on
	// `/bizy/chat/current_segment`; the value is transient (not derivable from
	// `messages`, since hydrated assistant/proactive turns carry no timestamp),
	// so we capture it here at load time. Consumers use it to decide whether to
	// show the new-segment greeter callout (BIZY-638).
	const [isNewSegment, setIsNewSegment] = useState(false);
	useEffect(() => {
		if (!isContinuous || continuousInitRef.current) return;
		// If we already have session data the self-heal effect below will handle it.
		if (sessionData?.conversationId && sessionData.messages?.length) {
			setDidInitLoad(true);
			return;
		}
		continuousInitRef.current = true;

		timeline
			.fetchCurrentSegment()
			.then(({ conversationId, isNewSegment: newSegment }) => {
				setIsNewSegment(newSegment);
				return timeline.loadNewest().then((messages) => {
					chat.loadMessages(messages, { conversationId, bizyEmotion: EMOTION_CURIOUS });
					setDidInitLoad(true);
				});
			})
			.catch(() => {
				// Silently fail — start with empty panel state.
				setDidInitLoad(true);
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isContinuous]);

	// Apply the fetched conversation. Coherence guard: wait for the detail that
	// matches the requested id (keepPreviousData can lag).
	useEffect(() => {
		if (!loadConversationId || !loadedConversation || loadedConversation.conversationId !== loadConversationId) return;
		refetchGenerationRef.current += 1;
		const hydrated = loadedConversation.messages.flatMap(hydrateBizyMessage);
		chat.loadMessages(hydrated, { conversationId: loadConversationId, bizyEmotion: EMOTION_CURIOUS });
		setLoadConversationId(undefined);
	}, [loadConversationId, loadedConversation, chat]);

	// Clear the queued load if the fetch failed.
	useEffect(() => {
		if (loadConversationId && isLoadError) setLoadConversationId(undefined);
	}, [loadConversationId, isLoadError]);

	const isLoadingConversation = Boolean(loadConversationId);

	// ── Refetch from backend (cross-tab sync, tab focus, mount self-heal) ──
	const refetchConversation = useCallback(
		async (convId: string) => {
			const generation = refetchGenerationRef.current;
			try {
				const res = await http.get(bizyConversationPath(convId), { headers: { 'Key-Inflection': 'camel' } });
				if (refetchGenerationRef.current !== generation) return;
				if (conversationIdRef.current !== convId) return;
				const detail = res.data.conversation as BizyUserConversationDetail;
				const hydrated = detail.messages.flatMap(hydrateBizyMessage);
				// Carry forward per-card choices the user already made (keyed by
				// message id) so a refetch doesn't reset post-click visual state.
				const choicesByMessageId = new Map<string, Record<number, ApprovalDecision>>();
				for (const msg of unionMessagesRef.current) {
					if (msg.role === 'assistant' && msg.id && msg.nodeChoices && Object.keys(msg.nodeChoices).length > 0) {
						choicesByMessageId.set(msg.id, msg.nodeChoices);
					}
				}
				const merged =
					choicesByMessageId.size === 0
						? hydrated
						: hydrated.map((msg) =>
								msg.role === 'assistant' && msg.id && choicesByMessageId.has(msg.id) ? { ...msg, nodeChoices: choicesByMessageId.get(msg.id) } : msg
							);
				chat.loadMessages(merged, { conversationId: convId, bizyEmotion: bizyEmotionRef.current });
			} catch {
				// Silently fail — the in-memory state is still usable.
			}
		},
		[http, chat]
	);

	// Tab focus: refetch to catch up on other tabs' messages.
	useEffect(() => {
		const handleVisibilityChange = () => {
			if (loadConversationId) return;
			if (document.visibilityState !== 'visible' || isLoadingRef.current) return;
			if (isContinuous) {
				if (!conversationIdRef.current) return;
				timeline
					.loadNewest()
					.then((messages) => {
						if (messages.length === 0) return;
						// Merge only genuinely new turns to preserve any older turns the
						// user had scrolled up to load.
						const newTurns = mergeNewTurns(unionMessagesRef.current, messages);
						if (newTurns.length === 0) return;
						const choicesByMessageId = new Map<string, Record<number, ApprovalDecision>>();
						for (const msg of unionMessagesRef.current) {
							if (msg.role === 'assistant' && msg.id && msg.nodeChoices && Object.keys(msg.nodeChoices).length > 0) {
								choicesByMessageId.set(msg.id, msg.nodeChoices);
							}
						}
						const combined = [...unionMessagesRef.current, ...newTurns];
						const merged =
							choicesByMessageId.size === 0
								? combined
								: combined.map((msg) =>
										msg.role === 'assistant' && msg.id && choicesByMessageId.has(msg.id) ? { ...msg, nodeChoices: choicesByMessageId.get(msg.id) } : msg
									);
						chat.loadMessages(merged, { conversationId: conversationIdRef.current, bizyEmotion: bizyEmotionRef.current as BizyState });
					})
					.catch(() => {});
			} else if (conversationIdRef.current) {
				refetchConversation(conversationIdRef.current);
			}
		};
		document.addEventListener('visibilitychange', handleVisibilityChange);
		return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
	}, [isContinuous, refetchConversation, loadConversationId, timeline, chat]);

	// Mount self-heal: validate cached session against the API. A successful
	// refetch supersedes content corrupted by out-of-order streaming; if the
	// cache had an unfinished assistant tail that the refetch didn't replace
	// (silent failure / never persisted), drop it.
	const isContinuousAtMountRef = useRef(isContinuous);
	useEffect(() => {
		if (!sessionData?.conversationId || !sessionData?.messages?.length) return;
		if (isContinuousAtMountRef.current) {
			// Continuous mode: refresh segment id + newest timeline page in parallel so
			// a stale cached conversationId (e.g. after a segment boundary) is corrected.
			continuousInitRef.current = true;
			Promise.all([timeline.fetchCurrentSegment(), timeline.loadNewest()])
				.then(([{ conversationId, isNewSegment: newSegment }, messages]) => {
					setIsNewSegment(newSegment);
					if (messages.length === 0) return;
					// Carry forward per-card choices the user already made.
					const choicesByMessageId = new Map<string, Record<number, ApprovalDecision>>();
					for (const msg of unionMessagesRef.current) {
						if (msg.role === 'assistant' && msg.id && msg.nodeChoices && Object.keys(msg.nodeChoices).length > 0) {
							choicesByMessageId.set(msg.id, msg.nodeChoices);
						}
					}
					const merged =
						choicesByMessageId.size === 0
							? messages
							: messages.map((msg) =>
									msg.role === 'assistant' && msg.id && choicesByMessageId.has(msg.id) ? { ...msg, nodeChoices: choicesByMessageId.get(msg.id) } : msg
								);
					chat.loadMessages(merged, { conversationId });
				})
				.catch(() => {});
		} else {
			// Legacy mode: refetch the specific conversation.
			const lastCached = sessionData.messages[sessionData.messages.length - 1];
			const lastWasPartial = lastCached.role === 'assistant' && !lastCached.id;
			refetchConversation(sessionData.conversationId).then(() => {
				if (lastWasPartial) chat.dropOpenAssistant();
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount with initial session data
	}, []);

	// ── Session persistence ───────────────────────────────────────────
	useEffect(() => {
		saveBizySession({ messages, conversationId: chat.conversationId, bizyEmotion: chat.bizyEmotion as BizyState });
	}, [messages, chat.conversationId, chat.bizyEmotion]);

	// ── Continuous-chat: load older turns (infinite-scroll upward) ───
	// `onBeforePrepend` is called synchronously just before the state update so
	// the scroll-anchor hook can capture scrollHeight before the DOM grows.
	const loadOlderTurns = useCallback(
		async (onBeforePrepend?: () => void) => {
			if (!isContinuous) return;
			const older = await timeline.loadOlder();
			if (older.length > 0) {
				onBeforePrepend?.();
				chat.prependMessages(older);
			}
		},
		[isContinuous, timeline, chat]
	);

	// ── Public actions (preserve legacy signatures) ───────────────────
	const sendMessage = useCallback(
		(
			content: string,
			perMessageExtraContext?: Record<string, string>,
			displayOptions?: { attachmentPreviewUrl?: string; userNodes?: BizyUserMessageNode[] }
		) => {
			refetchGenerationRef.current += 1;
			markBizyActivity();
			chat.sendMessage(content, {
				extraContext: perMessageExtraContext,
				attachmentPreviewUrl: displayOptions?.attachmentPreviewUrl,
				userNodes: displayOptions?.userNodes,
			});
		},
		[chat]
	);

	// Send a message that opens a NEW backend conversation (BIZY-683 blank
	// slate, classic mode). The visible conversation is archived above a time
	// divider, then the connector's `resetChat` clears its `conversationIdRef`
	// synchronously so the send POSTs without a conversation_id and the server
	// allocates a fresh one. The archive+reset+send pairing lives here — next
	// to the adapter that owns conversation identity — so callers can't get
	// the ordering wrong.
	const sendMessageInNewConversation = useCallback(
		(
			content: string,
			perMessageExtraContext?: Record<string, string>,
			displayOptions?: { attachmentPreviewUrl?: string; userNodes?: BizyUserMessageNode[] }
		) => {
			refetchGenerationRef.current += 1;
			markBizyActivity();
			const visibleMessages = flatMessagesRef.current;
			if (visibleMessages.length > 0) {
				setArchivedSegments((previousSegments) => [
					...previousSegments,
					{ messages: visibleMessages, boundaryAt: newestMessageTimestamp(visibleMessages) ?? new Date() },
				]);
			}
			setLoadConversationId(undefined);
			chat.resetChat();
			clearBizySession();
			chat.sendMessage(content, {
				extraContext: perMessageExtraContext,
				attachmentPreviewUrl: displayOptions?.attachmentPreviewUrl,
				userNodes: displayOptions?.userNodes,
			});
		},
		[chat]
	);

	const retryLastMessage = useCallback(() => {
		refetchGenerationRef.current += 1;
		markBizyActivity();
		chat.retryLastMessage();
	}, [chat]);

	// A reply finishing (or being cancelled) is the end of an exchange — the
	// idle window is measured from it, not from the user's send.
	const wasLoadingRef = useRef(false);
	useEffect(() => {
		if (wasLoadingRef.current && !chat.isLoading && flatMessagesRef.current.length > 0) markBizyActivity();
		wasLoadingRef.current = chat.isLoading;
	}, [chat.isLoading]);

	const cancelGeneration = useCallback((): string | undefined => {
		refetchGenerationRef.current += 1;
		return chat.cancelGeneration();
	}, [chat]);

	const resetChat = useCallback(() => {
		refetchGenerationRef.current += 1;
		setArchivedSegments([]);
		setLoadConversationId(undefined);
		chat.resetChat();
		clearBizySession();
	}, [chat]);

	useEffect(() => {
		window.addEventListener('pagehide', resetChat);
		return () => window.removeEventListener('pagehide', resetChat);
	}, [resetChat]);

	return {
		messages,
		conversationId: chat.conversationId,
		isLoading: chat.isLoading,
		isTimedOut: chat.isTimedOut,
		isOtherTabLoading: chat.isOtherTabLoading,
		isLoadingConversation,
		bizyEmotion: chat.bizyEmotion as BizyState,
		activeToolCalls: chat.activeToolCalls,
		sendMessage,
		sendMessageInNewConversation,
		cancelGeneration,
		resetChat,
		retryLastMessage,
		updateMessageFeedback: chat.updateMessageFeedback,
		updateNodeChoice: chat.updateNodeChoice,
		loadConversation,
		archivedSegments,
		// Continuous-chat additions
		isContinuous,
		didInitLoad,
		isNewSegment,
		hasMoreHistory: isContinuous ? timeline.hasMore : false,
		isLoadingOlder: timeline.isLoadingOlder,
		loadOlderTurns,
	};
}
